import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  DollarSign,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  TrendingUp,
  TrendingDown,
  Receipt,
  Users,
  Calendar,
  Search,
  Plus,
  Edit,
  Eye,
  Download,
} from 'lucide-react';
import { format } from 'date-fns';
import type {
  Subscription,
  SubscriptionPlan,
  BillingAnalytics,
  Invoice,
  PaymentMethod,
} from '@/types/entities/admin/admin';
import {
  GLASTONBURY_COMMUNITY_NAME,
  GLASTONBURY_PLATFORM_ID,
  glastonburyBillingAnalytics,
  glastonburyBillingEvents,
  glastonburyAssessmentSchedule,
  glastonburyPlatformAnalytics,
} from '@/data/entities/community_subpanel-elect/glastonburyPlatform';
import {
  glastonburyParcels,
  parcelStatistics,
  type ParcelBilling,
} from '@/data/entities/community_subpanel-elect/glastonburyParcels';

const subscriptionDataset: (Subscription & { tenantName: string })[] =
  glastonburyBillingEvents.subscriptions.map((subscription) => ({
    ...subscription,
    plan: {
      ...subscription.plan,
      tier: (['basic', 'premium', 'enterprise'].includes(subscription.plan.tier)
        ? subscription.plan.tier
        : 'basic') as 'basic' | 'premium' | 'enterprise',
      interval: (['month', 'year'].includes(subscription.plan.interval)
        ? subscription.plan.interval
        : 'month') as 'month' | 'year',
    },
    tenantName: GLASTONBURY_COMMUNITY_NAME,
  }));

const invoiceDataset: (Invoice & { tenantName: string })[] = glastonburyBillingEvents.invoices.map(
  (invoice) => ({
    ...invoice,
    tenantName: GLASTONBURY_COMMUNITY_NAME,
  }),
);

const analyticsDataset: BillingAnalytics = glastonburyBillingAnalytics;

const revenueHistory = glastonburyPlatformAnalytics.financialMetrics.revenueHistory;

interface BillingManagementProps {
  className?: string;
}

const BillingManagement: React.FC<BillingManagementProps> = ({ className }) => {
  const [subscriptions] = useState(subscriptionDataset);
  const [invoices] = useState(invoiceDataset);
  const [analytics] = useState(analyticsDataset);
  const [parcels] = useState(glastonburyParcels);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [segmentFilter, setSegmentFilter] = useState<string>('all');
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedParcel, setSelectedParcel] = useState<ParcelBilling | null>(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planModalMode, setPlanModalMode] = useState<'create' | 'edit'>('create');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'past_due':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'canceled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'unpaid':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'trialing':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'paid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'open':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'draft':
        return <Clock className="h-4 w-4 text-gray-500" />;
      case 'void':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      case 'uncollectible':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      active: 'default',
      trialing: 'secondary',
      past_due: 'destructive',
      canceled: 'destructive',
      unpaid: 'destructive',
      paid: 'default',
      open: 'secondary',
      draft: 'outline',
      void: 'outline',
      uncollectible: 'destructive',
    };

    return (
      <Badge variant={variants[status] || 'default'} className="flex items-center gap-1">
        {getStatusIcon(status)}
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
      </Badge>
    );
  };

  const getTierBadge = (tier: string) => {
    const colors: Record<string, string> = {
      basic: 'bg-blue-100 text-blue-800',
      premium: 'bg-purple-100 text-purple-800',
      enterprise: 'bg-amber-100 text-amber-800',
    };

    return (
      <Badge className={colors[tier] || 'bg-gray-100 text-gray-800'}>
        {tier.charAt(0).toUpperCase() + tier.slice(1)}
      </Badge>
    );
  };

  const formatCurrency = (amount: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);

  const formatTrend = (value: number) => (
    <div className={`flex items-center gap-1 ${value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
      {value >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      <span className="text-sm font-medium">{Math.abs(value)}%</span>
    </div>
  );

  const filteredParcels = parcels.filter((parcel) => {
    if (
      searchTerm &&
      !parcel.ownerName.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !parcel.parcelNumber.toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return false;
    }
    if (statusFilter && statusFilter !== 'all' && parcel.paymentStatus !== statusFilter) {
      return false;
    }
    if (segmentFilter && segmentFilter !== 'all' && parcel.segment !== segmentFilter) {
      return false;
    }
    return true;
  });

  const filteredSubscriptions = subscriptions.filter((sub) => {
    if (searchTerm && !sub.tenantName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (statusFilter && statusFilter !== 'all' && sub.status !== statusFilter) {
      return false;
    }
    return true;
  });

  const filteredInvoices = invoices.filter((invoice) => {
    if (searchTerm && !invoice.tenantName.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (statusFilter && statusFilter !== 'all' && invoice.status !== statusFilter) {
      return false;
    }
    return true;
  });

  const pieColors = ['#16a34a', '#2563eb', '#dc2626', '#ca8a04', '#9333ea'];

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Billing Management</h2>
          <p className="text-muted-foreground">
            Manage subscriptions, invoices, and revenue analytics
          </p>
        </div>
      </div>

      {/* Revenue Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(parcelStatistics.totalAnnualRevenue)}
                </p>
                {formatTrend(analytics.revenue.growth)}
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Collected Revenue</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(parcelStatistics.collectedRevenue)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {parcelStatistics.byPaymentStatus.paid} parcels
                </p>
              </div>
              <Receipt className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Parcels</p>
                <p className="text-2xl font-bold">{parcelStatistics.total}</p>
                <p className="text-sm text-muted-foreground">
                  {parcelStatistics.byPaymentStatus.overdue +
                    parcelStatistics.byPaymentStatus.payment_plan}{' '}
                  overdue/plans
                </p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(parcelStatistics.outstandingRevenue)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {parcelStatistics.byPaymentStatus.pending +
                    parcelStatistics.byPaymentStatus.overdue}{' '}
                  parcels
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="subscriptions" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="plans">Plans</TabsTrigger>
        </TabsList>

        <TabsContent value="subscriptions" className="space-y-6">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by parcel number or owner name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={segmentFilter} onValueChange={setSegmentFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Segment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Segments</SelectItem>
                    <SelectItem value="north">North Glastonbury</SelectItem>
                    <SelectItem value="south">South Glastonbury</SelectItem>
                    <SelectItem value="gav">Golden Age Village</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="payment_plan">Payment Plan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Parcel Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">North Glastonbury</p>
                    <p className="text-2xl font-bold">{parcelStatistics.bySegment.north}</p>
                    <p className="text-xs text-muted-foreground">parcels</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">South Glastonbury</p>
                    <p className="text-2xl font-bold">{parcelStatistics.bySegment.south}</p>
                    <p className="text-xs text-muted-foreground">parcels</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Golden Age Village</p>
                    <p className="text-2xl font-bold">{parcelStatistics.bySegment.gav}</p>
                    <p className="text-xs text-muted-foreground">parcels</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Parcels Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                Property Parcels ({filteredParcels.length} of {parcelStatistics.total})
              </CardTitle>
              <CardDescription>Annual landowner association dues billing</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Parcel Number</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Segment</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Annual Dues</TableHead>
                    <TableHead>Next Billing</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredParcels.slice(0, 50).map((parcel) => (
                    <TableRow key={parcel.id}>
                      <TableCell>
                        <div className="font-mono text-sm">{parcel.parcelNumber}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{parcel.ownerName}</div>
                        <div className="text-sm text-muted-foreground">{parcel.ownerEmail}</div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            parcel.segment === 'north'
                              ? 'border-blue-500 text-blue-700'
                              : parcel.segment === 'south'
                                ? 'border-purple-500 text-purple-700'
                                : 'border-amber-500 text-amber-700'
                          }
                        >
                          {parcel.segment === 'north'
                            ? 'North'
                            : parcel.segment === 'south'
                              ? 'South'
                              : 'GAV'}
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(parcel.paymentStatus)}</TableCell>
                      <TableCell>
                        <div className="font-medium">{formatCurrency(parcel.annualDues)}/year</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {format(new Date(parcel.nextBillingDate), 'MMM dd, yyyy')}
                        </div>
                        {parcel.lastPaymentDate && (
                          <div className="text-xs text-muted-foreground">
                            Last: {format(new Date(parcel.lastPaymentDate), 'MMM dd')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedParcel(parcel)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredParcels.length > 50 && (
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Showing 50 of {filteredParcels.length} parcels. Use filters to refine results.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="space-y-6">
          {/* Invoice Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search invoices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="void">Void</SelectItem>
                    <SelectItem value="uncollectible">Uncollectible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Invoices Table */}
          <Card>
            <CardHeader>
              <CardTitle>Invoices ({filteredInvoices.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice ID</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        <div className="font-mono text-sm">{invoice.id}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{invoice.tenantName}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {format(new Date(invoice.dueDate), 'MMM dd, yyyy')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedInvoice(invoice)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Churn Reasons */}
            <Card>
              <CardHeader>
                <CardTitle>Churn Analysis</CardTitle>
                <CardDescription>Reasons for subscription cancellations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analytics.churn.reasons.map((reason, index) => (
                    <div key={reason.reason} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{reason.reason}</span>
                        <span>
                          {reason.count} ({reason.percentage}%)
                        </span>
                      </div>
                      <Progress value={reason.percentage} />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Revenue Forecasts */}
            <Card>
              <CardHeader>
                <CardTitle>Revenue Forecasts</CardTitle>
                <CardDescription>Projected revenue based on current trends</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Next Month</span>
                    <span className="font-medium text-lg">
                      {formatCurrency(analytics.forecasts.nextMonthRevenue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Next Quarter</span>
                    <span className="font-medium text-lg">
                      {formatCurrency(analytics.forecasts.nextQuarterRevenue)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Year End</span>
                    <span className="font-bold text-xl">
                      {formatCurrency(analytics.forecasts.yearEndRevenue)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="plans" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Subscription Plans</CardTitle>
              <CardDescription>Annual landowner association dues structure</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Current Plan */}
                <Card className="border-2 border-primary">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold">Glastonbury Landowner Association</h3>
                        <p className="text-sm text-muted-foreground">Annual Membership Dues</p>
                      </div>
                      <Badge variant="default">Active</Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div>
                        <Label className="text-muted-foreground">Annual Dues per Parcel</Label>
                        <p className="text-3xl font-bold">{formatCurrency(494.4)}/year</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Total Parcels</Label>
                        <p className="text-3xl font-bold">406</p>
                      </div>
                    </div>

                    <div className="space-y-3 mb-6">
                      <h4 className="font-semibold">Membership Benefits & Features</h4>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Voting rights in association elections and governance matters</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Year-round road maintenance and snow removal services</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Access to community facilities and amenities</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>
                            Participation in community governance via Earthocracy platform
                          </span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Emergency road management and coordination</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Legal and compliance support for CCR enforcement</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Financial transparency and budget oversight</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span>Reserve fund contributions for long-term sustainability</span>
                        </li>
                      </ul>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-semibold mb-3">Plan Details</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <Label className="text-muted-foreground">Billing Cycle</Label>
                          <p className="font-medium">Annual</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Due Date</Label>
                          <p className="font-medium">March 31st</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Late Fee Policy</Label>
                          <p className="font-medium">$25 after 30 days</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Payment Plans</Label>
                          <p className="font-medium">Available upon request</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <Button
                        onClick={() => {
                          setPlanModalMode('edit');
                          setShowPlanModal(true);
                        }}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit Plan
                      </Button>
                      <Button variant="outline">
                        <Eye className="h-4 w-4 mr-2" />
                        View History
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Add New Plan Button */}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setPlanModalMode('create');
                    setShowPlanModal(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Additional Plan (Special Assessments)
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Parcel Details Dialog */}
      <Dialog open={!!selectedParcel} onOpenChange={() => setSelectedParcel(null)}>
        <DialogContent className="sm:max-w-[600px]">
          {selectedParcel && (
            <ParcelDetailsView parcel={selectedParcel} onClose={() => setSelectedParcel(null)} />
          )}
        </DialogContent>
      </Dialog>

      {/* Subscription Details Dialog */}
      <Dialog open={!!selectedSubscription} onOpenChange={() => setSelectedSubscription(null)}>
        <DialogContent className="sm:max-w-[600px]">
          {selectedSubscription && (
            <SubscriptionDetailsView
              subscription={selectedSubscription}
              onClose={() => setSelectedSubscription(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Details Dialog */}
      <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
        <DialogContent className="sm:max-w-[600px]">
          {selectedInvoice && (
            <InvoiceDetailsView
              invoice={selectedInvoice}
              onClose={() => setSelectedInvoice(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Plan Creation/Edit Modal */}
      <Dialog open={showPlanModal} onOpenChange={setShowPlanModal}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <PlanManagementModal mode={planModalMode} onClose={() => setShowPlanModal(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Parcel Details View Component
const ParcelDetailsView: React.FC<{
  parcel: ParcelBilling;
  onClose: () => void;
}> = ({ parcel, onClose }) => {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'payment_plan':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Parcel Details - {parcel.parcelNumber}
        </DialogTitle>
        <DialogDescription>Complete billing information for this property parcel</DialogDescription>
      </DialogHeader>

      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Parcel Number</Label>
            <p className="text-sm font-mono mt-1">{parcel.parcelNumber}</p>
          </div>
          <div>
            <Label>Payment Status</Label>
            <div className="mt-1">
              <Badge className={getStatusColor(parcel.paymentStatus)}>
                {parcel.paymentStatus.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium">Owner Information</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Owner Name</Label>
              <p className="text-sm mt-1">{parcel.ownerName}</p>
            </div>
            <div>
              <Label>Email</Label>
              <p className="text-sm mt-1">{parcel.ownerEmail}</p>
            </div>
            <div>
              <Label>Segment</Label>
              <p className="text-sm mt-1 capitalize">
                {parcel.segment === 'gav' ? 'Golden Age Village' : `${parcel.segment} Glastonbury`}
              </p>
            </div>
            <div>
              <Label>Property Type</Label>
              <p className="text-sm mt-1 capitalize">{parcel.propertyType}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium">Billing Information</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Annual Dues</Label>
              <p className="text-sm mt-1 font-medium">{formatCurrency(parcel.annualDues)}/year</p>
            </div>
            <div>
              <Label>Next Billing Date</Label>
              <p className="text-sm mt-1">
                {format(new Date(parcel.nextBillingDate), 'MMM dd, yyyy')}
              </p>
            </div>
            {parcel.lastPaymentDate && (
              <div className="col-span-2">
                <Label>Last Payment</Label>
                <p className="text-sm mt-1">
                  {format(new Date(parcel.lastPaymentDate), 'MMMM dd, yyyy')}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium">Membership Rights</h4>
          <div className="flex items-center gap-2">
            <CheckCircle
              className={`h-5 w-5 ${parcel.votingRights ? 'text-green-600' : 'text-gray-400'}`}
            />
            <span className="text-sm">
              Voting Rights: {parcel.votingRights ? 'Active' : 'Suspended'}
            </span>
          </div>
        </div>

        {parcel.notes && (
          <div className="space-y-2">
            <Label>Notes</Label>
            <p className="text-sm text-muted-foreground p-3 bg-muted rounded">{parcel.notes}</p>
          </div>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button>Send Payment Reminder</Button>
      </DialogFooter>
    </>
  );
};

// Plan Management Modal Component
const PlanManagementModal: React.FC<{
  mode: 'create' | 'edit';
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const [planData, setPlanData] = useState({
    name: mode === 'edit' ? 'Glastonbury Landowner Association' : 'Special Assessment',
    annualDues: mode === 'edit' ? 494.4 : 0,
    dueDate: mode === 'edit' ? 'March 31' : '',
    lateFee: mode === 'edit' ? 25 : 0,
    features:
      mode === 'edit'
        ? [
            'Voting rights in association elections and governance matters',
            'Year-round road maintenance and snow removal services',
            'Access to community facilities and amenities',
            'Participation in community governance via Earthocracy platform',
            'Emergency road management and coordination',
            'Legal and compliance support for CCR enforcement',
            'Financial transparency and budget oversight',
            'Reserve fund contributions for long-term sustainability',
          ]
        : [],
  });

  const [newFeature, setNewFeature] = useState('');
  const [showFeatureInput, setShowFeatureInput] = useState(false);

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setPlanData({
        ...planData,
        features: [...planData.features, newFeature.trim()],
      });
      setNewFeature('');
      setShowFeatureInput(false);
    }
  };

  const handleRemoveFeature = (index: number) => {
    setPlanData({
      ...planData,
      features: planData.features.filter((_, i) => i !== index),
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {mode === 'edit' ? 'Edit Association Dues Plan' : 'Create Special Assessment Plan'}
        </DialogTitle>
        <DialogDescription>
          {mode === 'edit'
            ? 'Configure annual membership dues and benefits for the Glastonbury Landowner Association'
            : 'Create a new special assessment plan for one-time or specific capital projects'}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="plan-name">Plan Name</Label>
            <Input
              id="plan-name"
              value={planData.name}
              onChange={(e) => setPlanData({ ...planData, name: e.target.value })}
              placeholder={
                mode === 'edit'
                  ? 'e.g., Annual Membership Dues'
                  : 'e.g., Road Paving Special Assessment'
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="annual-dues">
                {mode === 'edit' ? 'Annual Dues (per parcel)' : 'Assessment Amount (per parcel)'}
              </Label>
              <Input
                id="annual-dues"
                type="number"
                step="0.01"
                value={planData.annualDues}
                onChange={(e) =>
                  setPlanData({ ...planData, annualDues: parseFloat(e.target.value) })
                }
              />
            </div>
            <div>
              <Label htmlFor="due-date">
                {mode === 'edit' ? 'Annual Due Date' : 'Assessment Due Date'}
              </Label>
              <Input
                id="due-date"
                value={planData.dueDate}
                onChange={(e) => setPlanData({ ...planData, dueDate: e.target.value })}
                placeholder="e.g., March 31"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="late-fee">Late Fee (after 30 days)</Label>
            <Input
              id="late-fee"
              type="number"
              value={planData.lateFee}
              onChange={(e) => setPlanData({ ...planData, lateFee: parseInt(e.target.value) })}
            />
          </div>
        </div>

        <div className="space-y-3">
          <Label>
            {mode === 'edit' ? 'Membership Benefits & Features' : 'Assessment Scope & Benefits'}
          </Label>
          <p className="text-sm text-muted-foreground">
            {mode === 'edit'
              ? 'These benefits are included with annual membership'
              : 'What this assessment covers and benefits to the community'}
          </p>
          <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-3">
            {planData.features.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No features added yet. Click "Add Feature" below to start.
              </p>
            ) : (
              planData.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-2 text-sm group">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                  <span className="flex-1">{feature}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleRemoveFeature(index)}
                  >
                    <XCircle className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))
            )}
          </div>

          {showFeatureInput ? (
            <div className="flex gap-2">
              <Input
                placeholder="Enter new feature or benefit..."
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddFeature();
                  }
                }}
                autoFocus
              />
              <Button onClick={handleAddFeature} disabled={!newFeature.trim()}>
                Add
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowFeatureInput(false);
                  setNewFeature('');
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setShowFeatureInput(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Feature
            </Button>
          )}
        </div>

        <div className="bg-muted p-4 rounded-lg space-y-2">
          <h4 className="font-semibold">Plan Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Total Parcels:</span>
              <span className="ml-2 font-medium">406</span>
            </div>
            <div>
              <span className="text-muted-foreground">
                {mode === 'edit' ? 'Annual Revenue:' : 'Total Assessment:'}
              </span>
              <span className="ml-2 font-medium">
                $
                {(planData.annualDues * 406).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            // Handle save
            onClose();
          }}
        >
          {mode === 'edit' ? 'Save Changes' : 'Create Plan'}
        </Button>
      </DialogFooter>
    </>
  );
};

// Subscription Details View Component
const SubscriptionDetailsView: React.FC<{
  subscription: Subscription;
  onClose: () => void;
}> = ({ subscription, onClose }) => {
  const formatCurrency = (amount: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Subscription Details
        </DialogTitle>
        <DialogDescription>Detailed information about this subscription</DialogDescription>
      </DialogHeader>

      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Subscription ID</Label>
            <p className="text-sm font-mono mt-1">{subscription.id}</p>
          </div>
          <div>
            <Label>Status</Label>
            <div className="mt-1">
              <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                {subscription.status}
              </Badge>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium">Plan Details</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Plan Name</Label>
              <p className="text-sm mt-1">{subscription.plan.name}</p>
            </div>
            <div>
              <Label>Price</Label>
              <p className="text-sm mt-1">
                {formatCurrency(subscription.plan.price)}/{subscription.plan.interval}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium">Billing Period</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Current Period Start</Label>
              <p className="text-sm mt-1">
                {format(new Date(subscription.currentPeriodStart), 'MMM dd, yyyy')}
              </p>
            </div>
            <div>
              <Label>Current Period End</Label>
              <p className="text-sm mt-1">
                {format(new Date(subscription.currentPeriodEnd), 'MMM dd, yyyy')}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium">Plan Limits</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Max Users:</span>
                <span>{subscription.plan.limits.maxUsers.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>Max Storage:</span>
                <span>{subscription.plan.limits.maxStorage} GB</span>
              </div>
              <div className="flex justify-between">
                <span>Max Modules:</span>
                <span>{subscription.plan.limits.maxModules}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>API Calls:</span>
                <span>{subscription.plan.limits.apiCallsPerMonth.toLocaleString()}/month</span>
              </div>
              <div className="flex justify-between">
                <span>Priority Support:</span>
                <span>{subscription.plan.limits.prioritySupport ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span>Customization:</span>
                <span>{subscription.plan.limits.maxCustomization ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={onClose}>Close</Button>
      </DialogFooter>
    </>
  );
};

// Invoice Details View Component
const InvoiceDetailsView: React.FC<{
  invoice: Invoice;
  onClose: () => void;
}> = ({ invoice, onClose }) => {
  const formatCurrency = (amount: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Invoice Details
        </DialogTitle>
        <DialogDescription>Invoice #{invoice.id}</DialogDescription>
      </DialogHeader>

      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Invoice ID</Label>
            <p className="text-sm font-mono mt-1">{invoice.id}</p>
          </div>
          <div>
            <Label>Status</Label>
            <div className="mt-1">
              <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>
                {invoice.status}
              </Badge>
            </div>
          </div>
          <div>
            <Label>Amount</Label>
            <p className="text-sm mt-1 font-medium">
              {formatCurrency(invoice.amount, invoice.currency)}
            </p>
          </div>
          <div>
            <Label>Due Date</Label>
            <p className="text-sm mt-1">{format(new Date(invoice.dueDate), 'MMM dd, yyyy')}</p>
          </div>
        </div>

        {invoice.paidAt && (
          <div>
            <Label>Paid At</Label>
            <p className="text-sm mt-1">{format(new Date(invoice.paidAt), 'MMM dd, yyyy HH:mm')}</p>
          </div>
        )}

        <div className="space-y-4">
          <h4 className="font-medium">Invoice Items</h4>
          <div className="border rounded-lg">
            {invoice.items.map((item) => (
              <div key={item.id} className="p-4 border-b last:border-b-0">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{item.description}</p>
                    {item.period && (
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(item.period.start), 'MMM dd')} -{' '}
                        {format(new Date(item.period.end), 'MMM dd, yyyy')}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{formatCurrency(item.amount)}</p>
                    <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Download PDF
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogFooter>
    </>
  );
};

export default BillingManagement;
